const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in ms
const STORAGE_KEY = 'submittedLeads';
const SESSION_ID_KEY = 'cj_lead_session_id';
const LAST_LEAD_FORM_KEY = 'cj_last_lead_form';
/** Set when the PDP splash lead form has been shown or dismissed this tab session. */
const SPLASH_POPUP_SESSION_KEY = 'cj_splash_popup_consumed';

interface LeadSubmission {
	carId: string;
	carName: string;
	timestamp: number;
	formType?: string;
	leadType?: string;
	variantId?: string;
}

/**
 * Store a lead submission with timestamp and optional formType
 */
export const storeLeadSubmission = (carId: string, carName: string, formType?: string, variantId?: string): void => {
	try {
		const now = Date.now();
		const storedData = localStorage.getItem(STORAGE_KEY);
		let submittedLeads: LeadSubmission[] = storedData ? JSON.parse(storedData) : [];

		// Remove expired entries (older than 24 hours)
		submittedLeads = submittedLeads.filter((lead) => {
			return (now - lead.timestamp) < EXPIRY_TIME;
		});

		// Add new submission if not already exists
		const existing = submittedLeads.find((lead) => lead.carId === carId && (variantId ? lead.variantId === variantId : true));
		if (!existing) {
			submittedLeads.push({
				carId,
				...(variantId && { variantId }),
				carName,
				timestamp: now,
				formType,
				leadType: formType,
			});
		} else {
			// update timestamp and formType if already exists
			existing.timestamp = now;
			if (formType) existing.formType = formType;
			if (formType) existing.leadType = formType;
		}

		localStorage.setItem(STORAGE_KEY, JSON.stringify(submittedLeads));
	} catch (e) {
		console.error('Failed to store lead submission:', e);
	}
};

/**
 * Check if lead was submitted for a car (within 24 hours)
 */
export const hasSubmittedLead = (carId: string, leadType?: string, variantId?: string | undefined | null): boolean => {
	try {
		const now = Date.now();
		const storedData = localStorage.getItem(STORAGE_KEY);

		if (!storedData) return false;

		const submittedLeads: LeadSubmission[] = JSON.parse(storedData);

		// If leadType is provided, check for a matching carId + leadType entry.
		// If not provided, fallback to legacy behavior: check by carId only.
		const submission = leadType
			? submittedLeads.find((lead) => lead.carId === carId && lead.leadType === leadType && (variantId ? lead.variantId === variantId : true))
			: submittedLeads.find((lead) => lead.carId === carId && (variantId ? lead.variantId === variantId : true));

		// Check if exists and within 24 hours
		return submission ? (now - submission.timestamp) < EXPIRY_TIME : false;
	} catch (e) {
		console.error('Failed to check lead submission:', e);
		return false;
	}
};

/**
 * Get (or create) a random id scoped to this browser tab session, used so the
 * backend can recognize "this session already completed OTP verification once"
 * and skip asking again for other vehicles — cleared when the tab closes.
 */
export const getLeadSessionId = (): string => {
	if (typeof window === 'undefined') return '';
	try {
		let id = sessionStorage.getItem(SESSION_ID_KEY);
		if (!id) {
			id = typeof crypto !== 'undefined' && crypto.randomUUID
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
			sessionStorage.setItem(SESSION_ID_KEY, id);
		}
		return id;
	} catch (e) {
		console.error('Failed to get/create lead session id:', e);
		return '';
	}
};

interface LastLeadFormData {
	name: string;
	phone: string;
	location?: string;
	locationData?: Record<string, any>;
}

/**
 * Remember the last-submitted lead form values for this browser tab session,
 * so subsequent lead forms (for other vehicles) start prefilled instead of
 * asking the user to retype their name/phone/location every time.
 */
export const storeLastLeadFormData = (data: LastLeadFormData): void => {
	if (typeof window === 'undefined') return;
	try {
		sessionStorage.setItem(LAST_LEAD_FORM_KEY, JSON.stringify(data));
	} catch (e) {
		console.error('Failed to store last lead form data:', e);
	}
};

export const markSplashPopupConsumedForSession = (): void => {
	if (typeof window === 'undefined') return;
	try {
		sessionStorage.setItem(SPLASH_POPUP_SESSION_KEY, '1');
	} catch (e) {
		console.error('Failed to mark splash popup consumed:', e);
	}
};

export const hasSplashPopupBeenConsumedForSession = (): boolean => {
	if (typeof window === 'undefined') return false;
	try {
		return sessionStorage.getItem(SPLASH_POPUP_SESSION_KEY) === '1';
	} catch (e) {
		console.error('Failed to read splash popup session flag:', e);
		return false;
	}
};

export const getLastLeadFormData = (): LastLeadFormData | null => {
	if (typeof window === 'undefined') return null;
	try {
		const stored = sessionStorage.getItem(LAST_LEAD_FORM_KEY);
		return stored ? JSON.parse(stored) : null;
	} catch (e) {
		console.error('Failed to read last lead form data:', e);
		return null;
	}
};

/**
 * Clean up expired lead submissions
 */
export const cleanupExpiredLeads = (): void => {
	try {
		const now = Date.now();
		const storedData = localStorage.getItem(STORAGE_KEY);

		if (!storedData) return;

		const submittedLeads: LeadSubmission[] = JSON.parse(storedData);
		const validLeads = submittedLeads.filter((lead) => {
			return (now - lead.timestamp) < EXPIRY_TIME;
		});

		localStorage.setItem(STORAGE_KEY, JSON.stringify(validLeads));
	} catch (e) {
		console.error('Failed to cleanup expired leads:', e);
	}
};
